import { WorkoutSet, Workout } from '@strength/domain-workout';
import {
  BlockSelectionData,
  WorkoutGroupedData,
  ExerciseGroupedData,
  SectionGroupedData,
} from '../types';

export function hasDisabledItem(allSelectedData: WorkoutGroupedData[]) {
  return allSelectedData.some((d) =>
    d.data.some((d) => {
      if ('disabled' in d && d.disabled) {
        return d.disabled;
      }
      return false;
    }),
  );
}

function extractExerciseGroupedData(workout: Workout): ExerciseGroupedData[] {
  const workoutExercises: ExerciseGroupedData[] = [];

  for (const section of workout.sections) {
    for (const entry of section.entries) {
      if (entry.__type === 'WorkoutExercise') {
        workoutExercises.push({
          type: 'Exercise',
          includesMetrics: false,
          exercise: entry,
          sets: entry.sets ?? [],
          section: section,
          superset: null,
          disabled: false,
          alternateGroup: null,
        });
      } else if (entry.__type === 'WorkoutSuperset') {
        for (const supersetExercise of entry.exercises) {
          workoutExercises.push({
            type: 'Exercise',
            includesMetrics: false,
            exercise: supersetExercise.exercise,
            sets: supersetExercise.exercise.sets ?? [],
            section: section,
            superset: entry,
            disabled: false,
            alternateGroup: null,
          });
        }
      } else if (entry.__type === 'WorkoutAlternateGroup') {
        for (const alternateExercise of entry.exercises) {
          workoutExercises.push({
            type: 'Exercise',
            includesMetrics: false,
            exercise: alternateExercise.exercise,
            sets: alternateExercise.exercise.sets ?? [],
            section,
            superset: null,
            disabled: false,
            alternateGroup: entry,
          });
        }
      }
    }
  }

  return workoutExercises;
}

export function groupSelectionData(
  selections: readonly BlockSelectionData[],
): WorkoutGroupedData[] {
  const dataByWorkout = new Map<string, WorkoutGroupedData['data']>();

  for (const selection of selections) {
    const data = dataByWorkout.get(selection.workoutId);

    if (selection.type === 'Workout') {
      const workoutExercises = extractExerciseGroupedData(selection.workout);

      dataByWorkout.set(selection.workoutId, [
        {
          type: 'Workout',
          workout: selection.workout,
          sections: selection.workout.sections,
          exercises: workoutExercises,
          disabled: selection.disabled,
        },
      ]);
    } else if (selection.type === 'WorkoutName') {
      dataByWorkout.set(selection.workoutId, [
        {
          type: 'WorkoutName',
          workout: selection.workout,
          disabled: selection.disabled,
        },
      ]);
    } else if (selection.type === 'Set') {
      if (data) {
        const matchingExercise = findMatchingExercise(data, selection.exercise);
        if (matchingExercise) {
          matchingExercise.sets.push(selection.set);
        } else {
          data.push({
            type: 'Set',
            exercise: selection.exercise,
            set: selection.set,
            metrics: selection.metrics,
            disabled: selection.disabled,
          });
        }
      } else {
        dataByWorkout.set(selection.workoutId, [
          {
            type: 'Set',
            exercise: selection.exercise,
            set: selection.set,
            metrics: selection.metrics,
            disabled: selection.disabled,
          },
        ]);
      }
    } else if (selection.type === 'ExerciseMetric') {
      if (data) {
        const matchingExercise = findMatchingExercise(data, selection.exercise);
        if (matchingExercise) {
          matchingExercise.includesMetrics = true;
        } else {
          data.push({
            type: 'ExerciseMetric',
            exercise: selection.exercise,
            metric: selection.metric,
            disabled: selection.disabled,
          });
        }
      } else {
        dataByWorkout.set(selection.workoutId, [
          {
            type: 'ExerciseMetric',
            exercise: selection.exercise,
            metric: selection.metric,
            disabled: selection.disabled,
          },
        ]);
      }
    } else if (selection.type === 'Exercise') {
      if (data) {
        const newData: typeof data = [];
        let matchingSection: SectionGroupedData | undefined = undefined;
        const matchingSets: WorkoutSet[] = [];
        let includesMetrics = false;
        for (const d of data) {
          if (
            d.type === 'Set' &&
            d.exercise.workoutExerciseId ===
              selection.exercise.workoutExerciseId
          ) {
            matchingSets.push(d.set);
          } else if (
            d.type === 'AddSet' &&
            d.exercise.workoutExerciseId === selection.exercise.workoutSectionId
          ) {
            continue;
          } else if (
            d.type === 'ExerciseMetric' &&
            d.exercise.workoutExerciseId === selection.exercise.workoutSectionId
          ) {
            includesMetrics = true;
            continue;
          } else {
            newData.push(d);
          }

          if (
            d.type === 'Section' &&
            d.section.workoutSectionId === selection.exercise.workoutSectionId
          ) {
            matchingSection = d;
          }
        }

        const newExercise: ExerciseGroupedData = {
          type: 'Exercise',
          includesMetrics,
          exercise: selection.exercise,
          section: selection.section,
          superset: selection.superset,
          sets: matchingSets,
          disabled: selection.disabled,
          alternateGroup: selection.alternateGroup,
        };
        if (matchingSection) {
          matchingSection.exercises.push(newExercise);
        } else {
          newData.push(newExercise);
        }

        dataByWorkout.set(selection.workoutId, newData);
      } else {
        dataByWorkout.set(selection.workoutId, [
          {
            type: 'Exercise',
            includesMetrics: false,
            exercise: selection.exercise,
            section: selection.section,
            superset: selection.superset,
            sets: [],
            disabled: selection.disabled,
            alternateGroup: selection.alternateGroup,
          },
        ]);
      }
    } else if (selection.type === 'Section') {
      if (data) {
        const newData: typeof data = [];
        const matchingExercises: ExerciseGroupedData[] = [];
        for (const d of data) {
          if (
            d.type === 'Exercise' &&
            d.exercise.workoutSectionId === selection.section.workoutSectionId
          ) {
            matchingExercises.push(d);
          } else if (
            d.type === 'AddExercise' &&
            d.section.workoutSectionId === selection.section.workoutSectionId
          ) {
            continue;
          } else {
            newData.push(d);
          }
        }
        newData.push({
          type: 'Section',
          section: selection.section,
          exercises: matchingExercises,
          disabled: selection.disabled,
        });
        dataByWorkout.set(selection.workoutId, newData);
      } else {
        dataByWorkout.set(selection.workoutId, [
          {
            type: 'Section',
            section: selection.section,
            exercises: [],
            disabled: selection.disabled,
          },
        ]);
      }
    } else if (selection.type === 'AddSet') {
      if (data) {
        const matchingExercise = findMatchingExercise(data, selection.exercise);
        if (!matchingExercise) {
          dataByWorkout.set(
            selection.workoutId,
            data.concat({
              type: 'AddSet',
              exercise: selection.exercise,
            }),
          );
        }
      } else {
        dataByWorkout.set(selection.workoutId, [
          {
            type: 'AddSet',
            exercise: selection.exercise,
          },
        ]);
      }
    } else if (selection.type === 'AddExercise') {
      if (data) {
        const matchingSection = findMatchingSection(
          data,
          selection.section.workoutSectionId,
        );
        if (!matchingSection) {
          dataByWorkout.set(
            selection.workoutId,
            data.concat({
              type: 'AddExercise',
              section: selection.section,
              altGroupId: selection.altGroupId,
            }),
          );
        }
      } else {
        dataByWorkout.set(selection.workoutId, [
          {
            type: 'AddExercise',
            section: selection.section,
            altGroupId: selection.altGroupId,
          },
        ]);
      }
    } else if (selection.type === 'AddSection') {
      if (data) {
        dataByWorkout.set(
          selection.workoutId,
          data.concat({
            type: 'AddSection',
            workout: selection.workout,
          }),
        );
      } else {
        dataByWorkout.set(selection.workoutId, [
          {
            type: 'AddSection',
            workout: selection.workout,
          },
        ]);
      }
    }
  }
  return Array.from(dataByWorkout.entries()).map(([workoutId, data]) => ({
    workoutId,
    data,
  }));
}

function findMatchingExercise(
  data: WorkoutGroupedData['data'],
  {
    workoutExerciseId,
    workoutSectionId,
  }: { workoutSectionId: string; workoutExerciseId: string },
): ExerciseGroupedData | undefined {
  const match = data.find(
    (d): d is ExerciseGroupedData =>
      d.type === 'Exercise' &&
      d.exercise.workoutExerciseId === workoutExerciseId,
  );
  if (match) {
    return match;
  }
  return findMatchingSection(data, workoutSectionId)?.exercises.find(
    (e) => e.exercise.workoutExerciseId === workoutExerciseId,
  );
}

function findMatchingSection(
  data: WorkoutGroupedData['data'],
  workoutSectionId: string,
): SectionGroupedData | undefined {
  return data.find(
    (d): d is SectionGroupedData =>
      d.type === 'Section' && d.section.workoutSectionId === workoutSectionId,
  );
}
